import { toSentenceCase, missingURLHeader } from "@utils/string-helpers";
import {
  noSpaces,
  isLatin1Only,
  numericOnly,
  includesLineBreak,
  hasSpecialCharacter,
} from "@utils/string-helpers";
import {
  isWithinBounds,
  isValidEmail,
  isAlphanumeric,
} from "@utils/input-helpers";
import type {
  ApiErrorElements,
  ParsedError,
  ValidationErrorType,
  ValidationRule,
} from "@/shared/types";

export const STATIC_ERROR = {
  genericMessage: "There was an error prcessing your request",
  contact: "Please contact the Platform Team for further assistance.",
};

export const determineError = (responseObj: Record<string, any>): ParsedError => {
  let errorType: string | null = responseObj.hasOwnProperty("apiError")
    ? "apiError"
    : responseObj.hasOwnProperty("messages")
    ? "errorsArray"
    : responseObj.hasOwnProperty("error_elements")
    ? "error_elements"
    : null;
  let err: string | null =
    errorType === "apiError"
      ? responseObj.apiError.detail
      : errorType === "errorsArray"
      ? responseObj.messages.map((error: { message: string }) => error.message).join(", ")
      : errorType === "error_elements"
      ? responseObj.error_elements.details
      : null;
  let errorMsg: string | null =
    errorType === "error_elements" && !responseObj[errorType!].details
      ? errorDefinitions(responseObj as { status: number; error_elements: ApiErrorElements })
      : err;
  return { message: errorMsg, error: errorType ? responseObj[errorType] : null };
};

export const errorDefinitions = (errorInformation: { status: number; error_elements: ApiErrorElements }): string => {
  const { status, error_elements } = errorInformation;
  switch (status) {
    case 500:
      return (
        (error_elements?.exception_args?.[0]?.message &&
          `${error_elements["exception_args"][0]["message"]}`) ||
        `An unexpected error has occured.`
      );
    case 400:
      return `Invalid form information ${
        (error_elements["missing params"] &&
          `missing fields: ${error_elements["missing params"]}`) ||
        (error_elements["extra params"] &&
          `additional fields ${error_elements["extra params"]}`)
      }`;
    case 401:
      return `You are not authorized to make revisions.`;
    case 404:
      return `Not Found.`;
    default:
      return `An unexpected error has occured.`;
  }
};

export class ApiError extends Error {
  [key: string]: any;
  constructor(apiError: Record<string, any>) {
    super(typeof apiError === "string" ? apiError : JSON.stringify(apiError));
    let destructuredEr = determineError(apiError);
    Object.assign(this, destructuredEr.error);
    this.name = "API Error";
    this.message = toSentenceCase(destructuredEr.message);
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }
  }
}

export const userErrorMessage = (action?: string, feature?: string, error: string = ""): string => {
  return action
    ? `${toSentenceCase(
        action.endsWith("e") ? action.substring(0, action.length - 1) : action
      )}ing the ${feature} was unsuccessful. ${error ? `${error}. ` : ""}${
        STATIC_ERROR.contact
      }`
    : `${STATIC_ERROR.genericMessage} ${STATIC_ERROR.contact}`;
};

export const typeOfErrors: Record<ValidationErrorType, ValidationRule> = {
  noSpaces: {
    message: () => "This field cannot include any spaces.",
    errorCheck: (str: string) => (str ? !noSpaces(str) : false),
  },
  isLatin1Only: {
    message: () => "This field cannot include any special characters.",
    errorCheck: (str: string) => (str ? !isLatin1Only(str) : false),
  },
  limit: {
    message: (inputLimit) =>
      `This field cannot exceed ${inputLimit || "the specified"} characters.`,
    errorCheck: (str: string, inputLimit) => (str ? str.length > Number(inputLimit) : false),
  },
  hasEmailFormat: {
    message: () => "This field must be in valid email format.",
    errorCheck: (str: string) => (str ? !isValidEmail(str) : true),
  },
  required: {
    message: () => "This field is required.",
    errorCheck: (str: string) => !(str && str.length),
  },
  numericOnly: {
    message: () => "This field must be numbers only.",
    errorCheck: (str: string) => (str ? !numericOnly(str) : false),
  },
  isAlphanumeric: {
    message: () => "This field must be letters and numbers only.",
    errorCheck: (str: string) => (str ? !isAlphanumeric(str) : false),
  },
  includesLineBreak: {
    message: () => "This field cannot include line breaks.",
    errorCheck: (inputValue: string) =>
      inputValue ? includesLineBreak(inputValue) : false,
  },
  invalidURL: {
    message: () => "This field is an invalid URL.",
    errorCheck: (inputValue: string) =>
      inputValue ? missingURLHeader(inputValue) : false,
  },
  // TODO: Bug — uses ?? (nullish coalescing) instead of ternary ?. Always returns
  // the input string (truthy) instead of the boolean check result.
  hasSpecialCharacter: {
    message: () => "No special characters allowed.",
    // @ts-expect-error - needs to be fixed to return boolean, currently returns string due to nullish coalescing operator
    errorCheck: (inputValue: string) => inputValue ?? hasSpecialCharacter(inputValue),
  },
};

export const checkErrorTypes = (inputValue: string, errors?: ValidationErrorType[], inputLimit: number | string = ""): string => {
  let errorMessages = errors
    ? errors
        .map((errorType) =>
          typeOfErrors[errorType] &&
          typeOfErrors[errorType].hasOwnProperty("errorCheck")
            ? typeOfErrors[errorType].errorCheck(
                inputValue,
                errorType === "limit" && inputLimit
              ) &&
              typeOfErrors[errorType].message(
                errorType === "limit" && inputLimit
              )
            : ""
        )
        .filter((message) => message)
    : "";
  return errorMessages ? (errorMessages as string[]).join(" ") : "";
};
